import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  getTeahcherChatDataForUserVersion2,
  getStudentChatDataForUserWithLastMessage,
  markMessageAsSeen,
  getUnreadMessagesCount, // Add this import
} from "../../helper";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

const MessagesContext = createContext();

export const useMessages = () => {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error("useMessages must be used within a MessagesProvider");
  }
  return context;
};

export const MessagesProvider = ({ children }) => {
  dayjs.extend(relativeTime);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({}); // Store unread counts by chatId

  // Use useRef to store unsubscribe functions to avoid dependency issues
  const unsubscribeFunctionsRef = useRef({});

  // Helper function to truncate text after 3 words
  const truncateText = useCallback((text, maxWords = 3) => {
    const words = text.split(" ");
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(" ") + "...";
    }
    return text;
  }, []);

  // Helper function to truncate text by character length
  const truncateTextByLength = useCallback((text, maxLength = 30) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  }, []);

  // Function to fetch and update unread count for a specific chat
  const updateUnreadCount = useCallback(async (chatId, userId) => {
    try {
      const count = await getUnreadMessagesCount(chatId, userId);
      setUnreadCounts(prev => ({
        ...prev,
        [chatId]: count
      }));
      return count;
    } catch (error) {
      console.error("Error updating unread count:", error);
      return 0;
    }
  }, []);

  // Function to handle student messages
  const fetchStudentMessages = useCallback(
    async (loggedInUser) => {
      if (!loggedInUser?.uuid) {
        console.warn("No logged in user UUID provided");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        getStudentChatDataForUserWithLastMessage(
          loggedInUser?.uuid,
          async (chats) => {
            const sortedChats = chats.sort(
              (b, a) =>
                b.lastMessage.timestamp.seconds -
                a.lastMessage.timestamp.seconds
            );

            const messagePromises = sortedChats
              ?.filter(
                (msg) =>
                  msg.lastMessage.message_type === "TEXT" &&
                  msg.lastMessage.senderName !== loggedInUser?.fullName
              )
              .map(async (msg) => {
                // Get unread count for this chat
                const unreadCount = await updateUnreadCount(msg.chatId, loggedInUser?.id);

                // Convert UTC timestamp to Pakistan time
                const pakistanTime = dayjs(msg.lastMessage.timestamp);
                return {
                  chatId: msg.chatId,
                  lastMessage: msg.lastMessage,
                  id:
                    msg.lastMessage.receiverId === loggedInUser?.id
                      ? msg.lastMessage.senderId
                      : msg.lastMessage.receiverId,
                  uuid:
                    msg.lastMessage.receiver_uuid === loggedInUser?.uuid
                      ? msg.lastMessage.sender_uuid
                      : msg.lastMessage.receiver_uuid,
                  fullName:
                    msg.lastMessage.receiver_uuid === loggedInUser?.uuid
                      ? msg.lastMessage.senderName
                      : msg.lastMessage.receiverName,
                  seen: msg?.lastMessage?.seen,
                  senderName: msg.lastMessage.senderName,
                  message: truncateText(msg.lastMessage.text),
                  timestamp: pakistanTime.fromNow(),
                  unreadCount: unreadCount, // Add unread count to each message object
                };
              });

            // console.log("student message text", message_text);
            const message_text = await Promise.all(messagePromises);
            setMessages(message_text);
            setLoading(false);
          }
        );
      } catch (err) {
        console.error("Error fetching student messages:", err);
        setError(err.message);
        setLoading(false);
      }
    },
    [truncateText, updateUnreadCount]
  );

  // Function to handle teacher messages
  const fetchTeacherMessages = useCallback(
    async (loggedInUser) => {
      if (!loggedInUser?.uuid) {
        console.warn("No logged in user UUID provided");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const unsubscribeFuncs = getTeahcherChatDataForUserVersion2(
          loggedInUser?.uuid,
          async (chats) => {
            // console.log("all teacher chats", chats);

            const sortedChats = chats.sort(
              (b, a) =>
                b.lastMessage.timestamp.seconds -
                a.lastMessage.timestamp.seconds
            );

            const messagePromises = sortedChats
              ?.filter((msg) => msg.lastMessage.message_type === "TEXT")
              .map(async (msg) => {
                const unreadCount = await updateUnreadCount(msg.chatId, loggedInUser?.id);
                // Convert UTC timestamp to Pakistan time
                const pakistanTime = dayjs(msg.lastMessage.timestamp);
                return {
                  chatId: msg.chatId,
                  lastMessage: msg.lastMessage,
                  id:
                    msg.lastMessage.receiverId === loggedInUser?.id
                      ? msg.lastMessage.senderId
                      : msg.lastMessage.receiverId,
                  uuid:
                    msg.lastMessage.receiver_uuid === loggedInUser?.uuid
                      ? msg.lastMessage.sender_uuid
                      : msg.lastMessage.receiver_uuid,
                  fullName:
                    msg.lastMessage.receiver_uuid === loggedInUser?.uuid
                      ? msg.lastMessage.senderName
                      : msg.lastMessage.receiverName,
                  seen: msg?.lastMessage?.seen,
                  senderName: msg.lastMessage.senderName,
                  message: truncateTextByLength(msg.lastMessage.text),
                  timestamp: pakistanTime.fromNow(), // "5 minutes ago", "2 hours ago", etc.
                  unreadCount: unreadCount, // Add unread count to each message object
                };
              });

            // console.log("teacher message text", message_text);

            const message_text = await Promise.all(messagePromises);
            // console.log("unreadcount", unreadCount);

            setMessages(message_text);
            setLoading(false);
          }
        );

        // Store unsubscribe functions in ref
        unsubscribeFunctionsRef.current = unsubscribeFuncs;
      } catch (err) {
        console.error("Error fetching teacher messages:", err);
        setError(err.message);
        setLoading(false);
      }
    },
    [truncateTextByLength, updateUnreadCount]
  );

  // Clean up existing subscriptions
  const cleanupSubscriptions = useCallback(() => {
    if (unsubscribeFunctionsRef.current.unsubscribeSender) {
      unsubscribeFunctionsRef.current.unsubscribeSender();
    }
    if (unsubscribeFunctionsRef.current.unsubscribeReceiver) {
      unsubscribeFunctionsRef.current.unsubscribeReceiver();
    }
    unsubscribeFunctionsRef.current = {};
  }, []);

  // Main function to fetch messages based on user role
  const fetchMessages = useCallback(
    (loggedInUser) => {
      if (!loggedInUser?.uuid) {
        console.warn("No logged in user UUID provided");
        return;
      }

      // Clean up existing subscriptions
      cleanupSubscriptions();

      // Fetch messages based on user role
      if (loggedInUser.role === "student") {
        fetchStudentMessages(loggedInUser);
      } else if (loggedInUser.role === "teacher") {
        fetchTeacherMessages(loggedInUser);
      } else {
        console.warn("Unknown user role:", loggedInUser.role);
        setError("Unknown user role");
        setLoading(false);
      }
    },
    [fetchStudentMessages, fetchTeacherMessages, cleanupSubscriptions]
  );

  const markMessageAsRead = useCallback(async (chatId, userId) => {
    try {
      // Update in backend
      await markMessageAsSeen(chatId);

      // Update local state
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.chatId === chatId
            ? {
                ...msg,
                seen: true,
                unreadCount: 0, // Reset unread count
                lastMessage: {
                  ...msg.lastMessage,
                  seen: true,
                },
              }
            : msg
        )
      );

      // Update unread counts
      setUnreadCounts(prev => ({
        ...prev,
        [chatId]: 0
      }));
    } catch (error) {
      console.error("Error marking message as read:", error);
      setError(error.message);
    }
  }, []);

  const markMultipleMessagesAsRead = useCallback(async (chatIds, userId) => {
    try {
      // Update in backend
      await Promise.all(chatIds.map((id) => markMessageAsSeen(id)));

      // Update local state
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          chatIds.includes(msg.chatId)
            ? {
                ...msg,
                seen: true,
                unreadCount: 0, // Reset unread count
                lastMessage: {
                  ...msg.lastMessage,
                  seen: true,
                },
              }
            : msg
        )
      );

      // Update unread counts
      setUnreadCounts(prev => {
        const newCounts = { ...prev };
        chatIds.forEach(chatId => {
          newCounts[chatId] = 0;
        });
        return newCounts;
      });
    } catch (error) {
      console.error("Error marking multiple messages as read:", error);
      setError(error.message);
    }
  }, []);

  const addMessage = useCallback((newMessage) => {
    setMessages((prevMessages) => [newMessage, ...prevMessages]);
  }, []);

  const updateMessage = useCallback((chatId, updates) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.chatId === chatId ? { ...msg, ...updates } : msg
      )
    );
  }, []);
  // Function to refresh unread counts for all chats
  const refreshUnreadCounts = useCallback(async (loggedInUser) => {
    if (!loggedInUser?.id) return;
    
    try {
      const countsPromises = messages.map(msg => 
        updateUnreadCount(msg.chatId, loggedInUser.id)
      );
      await Promise.all(countsPromises);
    } catch (error) {
      console.error("Error refreshing unread counts:", error);
    }
  }, [messages, updateUnreadCount]);

  // Get total unread count
  const getUnreadCount = useCallback(
    (loggedInUser) => {
      if (!loggedInUser?.id) return 0;
      return (
        messages?.filter(
          (msg) =>
            msg?.lastMessage?.receiverId === loggedInUser?.id && !msg.seen
        )?.length || 0
      );
    },
    [messages]
  );

  // Get total unread count across all chats
  const getTotalUnreadCount = useCallback(() => {
    return Object.values(unreadCounts).reduce((total, count) => total + count, 0);
  }, [unreadCounts]);

  // Get unread count for specific chat
  const getUnreadCountForChat = useCallback(
    (chatId) => {
    return unreadCounts[chatId] || 0;
  }, [unreadCounts]);

  // Clear all messages (useful for logout)
  const clearMessages = useCallback(() => {
    cleanupSubscriptions();
    setMessages([]);
    setUnreadCounts({});
    setError(null);
  }, [cleanupSubscriptions]);

  // Refresh messages (useful for manual refresh)
  const refreshMessages = useCallback(
    (loggedInUser) => {
      fetchMessages(loggedInUser);
    },
    [fetchMessages]
  );

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      messages,
      loading,
      error,
      fetchMessages,
      markMessageAsRead,
      markMultipleMessagesAsRead,
      unreadCounts,
      refreshUnreadCounts,
      getTotalUnreadCount,
      addMessage,
      updateMessage,
      getUnreadCount,
      getUnreadCountForChat,
      clearMessages,
      refreshMessages,
      setMessages, // In case you need direct access
    }),
    [
      messages,
      loading,
      error,
      fetchMessages,
      markMessageAsRead,
      markMultipleMessagesAsRead,
      unreadCounts,
      refreshUnreadCounts,
      getTotalUnreadCount,
      addMessage,
      updateMessage,
      getUnreadCount,
      getUnreadCountForChat,
      clearMessages,
      refreshMessages,
    ]
  );

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
};
